import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, styled } from "@mui/material";

import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import LetsTalk from "../../components/letsTalk";
import PageTitle from "../../components/PageTitle";

import EnhancedHeroSection from "./HeroSection";
import EnhancedProjectsSection, { ProjectDataItem } from "./ProjectsSection";
import ServicesSection from "./ServicesSection";
import EnhancedAboutSection from "./AboutSection";
import IndustriesSection from "./IndustriesSection";
import TechnologiesSection from "./TechnologiesSection";
import WhyChooseUsSection from "./WhyChooseUsSection";
import EnhancedFAQSection, { FAQItem } from "./FAQSection";

import ProjectCard1 from "../../assests/images/Home/projectCard1.svg";
import ProjectCard2 from "../../assests/images/Home/projectCard2.svg";
import ProjectCard3 from "../../assests/images/Home/projectCard3.svg";

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 720,
});

const PROJECTS: ProjectDataItem[] = [
  {
    image: ProjectCard1,
    projectName: "Nationwide vehicle transport platform",
    description:
      "Customer booking flow, driver dispatch dashboard, and real-time shipment tracking — built from scratch for a US-based vehicle transport company.",
    technologies: ["React", "Node.js", "MongoDB", "Stripe"],
    industry: "Logistics",
    client: "Shypn",
    role: "Engineering partner",
    location: "USA",
    quote:
      "They go beyond simply executing a task — they're genuinely interested in understanding what the end user needs.",
  },
  {
    image: ProjectCard2,
    projectName: "AI-powered vocal training app",
    description:
      "Real-time pitch and tone feedback paired with adaptive lesson progression. Audio signal processing pipeline built for iOS and Android in 14 weeks.",
    technologies: ["React Native", "TensorFlow", "Firebase"],
    industry: "Education / AI",
    client: "Utaren",
    role: "Product engineering",
    location: "USA",
    quote:
      "They work to help develop our company, not just be a third-party service provider.",
  },
  {
    image: ProjectCard3,
    projectName: "Trading & portfolio management platform",
    description:
      "Live market data, order execution, and position tracking. Full web client and broker-integration layer with sub-second update latency.",
    technologies: ["Angular", "Node.js", "PostgreSQL", "WebSocket"],
    industry: "Fintech",
    client: "Trade Locker",
    role: "Chief Architect",
    location: "USA",
    quote:
      "The reliability and quality of the work done by the team are impressive.",
  },
];

const FAQS: FAQItem[] = [
  {
    question: "What type of software development services do you offer?",
    answer:
      "End-to-end product development covering web, mobile, and backend systems — discovery, UI/UX design, engineering, AI/ML integration, cloud, and QA. We tailor solutions to your business needs and deliver against a milestone timeline.",
  },
  {
    question: "Which industries do you have experience with?",
    answer:
      "Fintech, healthcare, SaaS, logistics, education, and e-commerce. That diverse background means we understand the specific compliance, performance, and integration challenges of your domain.",
  },
  {
    question: "How long does a typical project take?",
    answer:
      "MVPs ship in 6–12 weeks. Full products typically run 3–6 months. AI integrations on top of an existing app can launch in 2–4 weeks if your data is ready. Every engagement comes with a milestone-based timeline.",
  },
  {
    question: "What does an engagement cost?",
    answer:
      "Focused sprints start at $5,000 USD. Most full-build projects run $15,000–$80,000+ depending on scope and complexity. We offer fixed-price, time & materials, and dedicated-team models — whichever fits your situation best.",
  },
  {
    question: "Who owns the code and IP?",
    answer:
      "You do. 100% IP transfer on final payment. That includes source code, infrastructure credentials, documentation, design files, and any models we train. Everything is yours, in writing.",
  },
  {
    question: "Can you help modernize existing software or systems?",
    answer:
      "Yes. We specialize in application modernization — upgrading legacy systems with modern architectures and technologies to improve performance, security, scalability, and maintainability without breaking existing workflows.",
  },
  {
    question: "How do we start a project with you?",
    answer:
      "Book a free 30-minute discovery call. Within 48 hours you'll receive a written proposal that includes scope, milestones, timeline, and a fixed-price or T&M estimate. No obligation.",
  },
  {
    question: "Where is your team based?",
    answer:
      "Two offices: Wilson Ave in Timmins, Ontario, Canada, and Johar Town in Lahore, Pakistan. Our delivery model gives North American clients senior engineering at competitive rates with overlapping working hours.",
  },
];

const ExpertiseIntro: React.FC = () => (
  <Box
    id="services"
    component="section"
    aria-labelledby="services-heading"
    sx={{ pt: { xs: 8, md: 11 }, pb: 0, backgroundColor: "#fff" }}
  >
    <Container maxWidth="lg">
      <Box sx={{ mb: { xs: 5, md: 7 } }}>
        <Label>Expertise</Label>
        <SectionTitle id="services-heading" variant="h2">
          Expertise to power up your product
        </SectionTitle>
        <Lead>
          From discovery sprints to long-term engineering partnerships —
          AI/ML, custom software, mobile, cloud, design, and security under
          one roof. Senior teams from kickoff to launch.
        </Lead>
      </Box>
    </Container>
  </Box>
);

const Home: React.FC = () => {
  const navigate = useNavigate();
  const handleStartProject = () => navigate("/contacts");
  const handleLearnMore = () => navigate("/portfolio");
  const handleMoreAboutUs = () => navigate("/services");

  return (
    <React.Fragment>
      <PageTitle />
      <Navbar />

      <EnhancedHeroSection
        onStartProject={handleStartProject}
        onLearnMore={handleLearnMore}
      />

      <EnhancedProjectsSection projectData={PROJECTS} />

      <ExpertiseIntro />
      <ServicesSection />

      <EnhancedAboutSection onMoreAboutUs={handleMoreAboutUs} />

      <IndustriesSection />

      <TechnologiesSection />

      <WhyChooseUsSection />

      <EnhancedFAQSection faqData={FAQS} />

      <LetsTalk />
      <Footer />
    </React.Fragment>
  );
};

export default Home;
